import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  BarChart3,
  Calendar,
  Check,
  Globe2,
  Mail,
  Menu,
  Paperclip,
  Sparkles,
  Sun,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { PLAN_CONFIGS, isContactUsPlan, type PlanType } from '@/config/plans';
import { BlogPreviewSection } from '@/components/landing/BlogPreviewSection';

const DEMO_CALENDAR_LINK = 'https://calendar.app.google/WHQXKirTooTrJg7R8';
const LINKEDIN_COMPANY_LINK = 'https://www.linkedin.com/company/solaipv';

type TFn = (key: string, options?: Record<string, unknown>) => string;

interface LandingPageProps {
  onGoToLogin: () => void;
  onGoToSignup: () => void;
}

function useIntersectionObserver(options = {}) {
  const [isInView, setIsInView] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, ...options }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  return { ref, isInView };
}

function AnimatedCounter({
  target,
  duration = 2000,
  suffix = '',
}: {
  target: number;
  duration?: number;
  suffix?: string;
}) {
  const [count, setCount] = useState(0);
  const { ref, isInView } = useIntersectionObserver();

  useEffect(() => {
    if (!isInView) return;

    let startTime: number;
    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(easeOut * target));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [isInView, target, duration]);

  return (
    <span ref={ref}>
      {count.toLocaleString()}
      {suffix}
    </span>
  );
}

function SunCursorEffect() {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (containerRef.current) {
        const x = e.clientX / window.innerWidth;
        containerRef.current.style.setProperty('--sun-x', String(x));
      }
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <div
      ref={containerRef}
      className="sun-cursor-effect"
      style={{ '--sun-x': 0.5 } as React.CSSProperties}
    >
      <div className="sun-glow" />
      <div className="sun-rays" />
    </div>
  );
}

function SolarGridBackground() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="absolute inset-0 solar-grid-pattern opacity-50" />
      <div
        className="absolute -left-1/4 -top-1/4 h-[800px] w-[800px] rounded-full blur-[120px]"
        style={{
          background: 'radial-gradient(circle, hsl(51 100% 50% / 0.15) 0%, transparent 70%)',
        }}
      />
      <div
        className="absolute -right-1/4 top-1/4 h-[600px] w-[600px] rounded-full blur-[100px]"
        style={{
          background: 'radial-gradient(circle, hsl(51 100% 50% / 0.1) 0%, transparent 70%)',
        }}
      />
    </div>
  );
}

function HeroVisual({ t }: { t: TFn }) {
  return (
    <div className="relative">
      <div className="absolute -inset-2 sm:-inset-4 rounded-2xl sm:rounded-3xl bg-gradient-to-br from-primary/20 via-transparent to-primary/10 blur-xl sm:blur-2xl" />
      <div className="relative overflow-hidden rounded-xl sm:rounded-2xl border-2 border-foreground bg-card shadow-xl sm:shadow-2xl">
        <picture>
          <source
            type="image/webp"
            srcSet="/images/hero-illustration-400w.webp 400w, /images/hero-illustration-524w.webp 524w, /images/hero-illustration-800w.webp 800w"
            sizes="(max-width: 640px) 400px, (max-width: 1024px) 524px, 800px"
          />
          <img
            src="/images/hero-illustration.jpg"
            alt="Solar PV engineering dashboard"
            className="h-auto w-full object-cover"
            loading="eager"
            fetchPriority="high"
            width={800}
            height={600}
            decoding="async"
          />
        </picture>
      </div>

      <div className="absolute -right-2 -top-2 sm:-right-4 sm:-top-4 animate-float">
        <div className="rounded-lg sm:rounded-xl border-2 border-foreground bg-primary px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm font-bold text-primary-foreground shadow-lg">
          <Sparkles className="mr-0.5 sm:mr-1 inline h-3 w-3 sm:h-3.5 sm:w-3.5" />
          {t('landing-hero-visual-badge') || 'AI solar expert'}
        </div>
      </div>
    </div>
  );
}

function BeforeAfterSection({ t }: { t: TFn }) {
  const { ref, isInView } = useIntersectionObserver();

  return (
    <section ref={ref} className="landing-section py-16 sm:py-20 md:py-28">
      <div className="mx-auto max-w-6xl px-4">
        <div
          className={cn(
            'mb-8 sm:mb-12 text-center transition-all duration-1000',
            isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          )}
        >
          <Badge
            variant="secondary"
            className="mb-3 sm:mb-4 border-2 border-foreground text-xs sm:text-sm"
          >
            {t('landing-story-eyebrow') || 'The transformation'}
          </Badge>
          <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl md:text-4xl lg:text-5xl">
            {t('landing-story-title') || 'From chaos to clarity'}
          </h2>
        </div>

        <div className="grid gap-6 sm:gap-8 lg:grid-cols-2">
          <div
            className={cn(
              'group relative rounded-xl sm:rounded-2xl border-2 border-border bg-muted/30 p-5 sm:p-8 transition-all duration-700',
              isInView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'
            )}
          >
            <div className="absolute -top-3 left-4 sm:left-6">
              <Badge
                variant="outline"
                className="bg-background text-muted-foreground text-xs sm:text-sm"
              >
                {t('landing-before') || 'Before'}
              </Badge>
            </div>
            <ul className="space-y-3 sm:space-y-4 pt-2">
              {[
                t('landing-before-1') || 'Scattered spreadsheets across folders',
                t('landing-before-2') || 'Manual calculations prone to errors',
                t('landing-before-3') || 'Copy-paste reports that look unprofessional',
                t('landing-before-4') || 'No single source of truth for project data',
              ].map((item, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 sm:gap-3 text-muted-foreground text-sm sm:text-base"
                >
                  <div className="mt-1.5 sm:mt-2 h-1.5 w-1.5 sm:h-2 sm:w-2 shrink-0 rounded-full bg-muted-foreground/50" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div
            className={cn(
              'group relative rounded-xl sm:rounded-2xl border-2 border-primary bg-primary/5 p-5 sm:p-8 transition-all duration-700 delay-200',
              isInView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'
            )}
          >
            <div className="absolute -top-3 left-4 sm:left-6">
              <Badge className="border-2 border-foreground bg-primary text-primary-foreground text-xs sm:text-sm">
                {t('landing-after') || 'After'}
              </Badge>
            </div>
            <ul className="space-y-3 sm:space-y-4 pt-2">
              {[
                t('landing-after-1') || 'One unified workspace for everything',
                t('landing-after-2') || 'Automated sizing with reliable meteo data',
                t('landing-after-3') || 'Professional reports generated instantly',
                t('landing-after-4') || 'Complete project history and collaboration',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2 sm:gap-3 text-sm sm:text-base">
                  <div className="mt-0.5 flex h-4 w-4 sm:h-5 sm:w-5 shrink-0 items-center justify-center rounded-full border-2 border-foreground bg-primary">
                    <Check className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-primary-foreground" />
                  </div>
                  <span className="font-medium">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function FeatureSection({
  icon: Icon,
  title,
  description,
  visual,
  reverse,
  index,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  visual: React.ReactNode;
  reverse?: boolean;
  index: number;
}) {
  const { ref, isInView } = useIntersectionObserver();

  return (
    <div
      ref={ref}
      className={cn(
        'grid items-center gap-8 py-12 lg:grid-cols-2 lg:gap-16',
        reverse && 'lg:[&>*:first-child]:order-2'
      )}
    >
      <div
        className={cn(
          'transition-all duration-700',
          isInView
            ? 'opacity-100 translate-x-0'
            : `opacity-0 ${reverse ? 'translate-x-12' : '-translate-x-12'}`
        )}
        style={{ transitionDelay: `${index * 100}ms` }}
      >
        <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl border-2 border-foreground bg-primary/20">
          <Icon className="h-7 w-7" />
        </div>
        <h3 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">{title}</h3>
        <p className="mt-3 text-lg text-muted-foreground leading-relaxed">{description}</p>
      </div>
      <div
        className={cn(
          'transition-all duration-700 delay-200',
          isInView ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
        )}
      >
        {visual}
      </div>
    </div>
  );
}

function FreeStudySection({ t }: { t: TFn }) {
  const { ref, isInView } = useIntersectionObserver();
  const [location, setLocation] = useState('');
  const [systemType, setSystemType] = useState('');
  const [consumption, setConsumption] = useState('');
  const [notes, setNotes] = useState('');

  const isTotalInjection = systemType === 'total-injection';

  const systemTypeLabel = systemType
    ? t(`landing-free-study-system-type-${systemType}`) || systemType
    : '';

  const handleSubmit = () => {
    const subject = t('landing-free-study-email-subject') || 'Free Solar Feasibility Study Request';
    const body = [
      `${t('landing-free-study-field-location') || 'Location'}: ${location}`,
      `${t('landing-free-study-field-system-type') || 'System type'}: ${systemTypeLabel}`,
      !isTotalInjection && consumption
        ? `${t('landing-free-study-field-consumption') || 'Annual consumption'}: ${consumption}`
        : '',
      notes ? `${t('landing-free-study-field-notes') || 'Additional notes'}: ${notes}` : '',
      '',
      `--- ${t('landing-free-study-attachment-hint') || 'Feel free to attach any relevant files (electricity bills, roof photos, plans…) to this email.'} ---`,
    ]
      .filter((line) => line !== '')
      .join('\n');

    window.location.href = `mailto:contact@solaipv.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const benefits = [
    {
      title: t('landing-free-study-benefit-1-title') || 'System sizing',
      desc:
        t('landing-free-study-benefit-1-desc') ||
        'Recommended panels, inverter, and system capacity',
    },
    {
      title: t('landing-free-study-benefit-2-title') || 'Production estimate',
      desc:
        t('landing-free-study-benefit-2-desc') ||
        'Expected annual energy output based on your location',
    },
    {
      title: t('landing-free-study-benefit-3-title') || 'ROI preview',
      desc: t('landing-free-study-benefit-3-desc') || 'Estimated payback period and annual savings',
    },
  ];

  const isFormValid = location.trim() && systemType && (isTotalInjection || consumption.trim());

  return (
    <section ref={ref} className="landing-section bg-foreground py-16 sm:py-20 md:py-28">
      <div className="mx-auto max-w-6xl px-4">
        <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
          {/* Left column — value proposition */}
          <div
            className={cn(
              'transition-all duration-1000',
              isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            )}
          >
            <Badge
              variant="secondary"
              className="mb-3 sm:mb-4 border-2 border-background/20 text-xs sm:text-sm"
            >
              <Sparkles className="mr-1.5 sm:mr-2 h-3 w-3 sm:h-3.5 sm:w-3.5" />
              {t('landing-free-study-eyebrow') || 'Free up to 10 kW'}
            </Badge>
            <h2 className="font-heading text-2xl font-bold tracking-tight text-background sm:text-3xl md:text-4xl lg:text-5xl">
              {t('landing-free-study-title') || 'Let us size your next project — on us'}
            </h2>
            <p className="mt-3 sm:mt-4 max-w-lg text-base sm:text-lg text-background/70">
              {t('landing-free-study-subtitle') ||
                'Share a few details about your project and receive a personalized feasibility report: system sizing, estimated production, and ROI preview.'}
            </p>

            <div className="mt-6 sm:mt-8 space-y-4">
              {benefits.map((benefit, i) => (
                <div
                  key={i}
                  className={cn(
                    'flex items-start gap-3 transition-all duration-700',
                    isInView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'
                  )}
                  style={{ transitionDelay: `${300 + i * 150}ms` }}
                >
                  <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary">
                    <Check className="h-3 w-3 text-primary-foreground" />
                  </div>
                  <div>
                    <div className="text-sm sm:text-base font-semibold text-background">
                      {benefit.title}
                    </div>
                    <div className="text-xs sm:text-sm text-background/60">{benefit.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 sm:mt-8 flex items-center gap-2 text-xs sm:text-sm text-background/60">
              <Mail className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0" />
              {t('landing-free-study-delivery') || 'Delivered to your inbox within 24 hours'}
            </div>
          </div>

          {/* Right column — form card */}
          <div
            className={cn(
              'relative transition-all duration-1000 delay-200',
              isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            )}
          >
            <div className="absolute -inset-4 rounded-3xl bg-primary/20 blur-2xl" />
            <Card className="relative border-2 border-background/20 shadow-2xl">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="text-lg sm:text-xl">
                  {t('landing-free-study-form-title') || 'Your project details'}
                </CardTitle>
                <CardDescription className="text-sm">
                  {t('landing-free-study-form-desc') ||
                    "Fill in what you know — we'll handle the rest."}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 p-4 pt-0 sm:p-6 sm:pt-0">
                <div className="space-y-1.5">
                  <label htmlFor="free-study-location" className="text-xs sm:text-sm font-medium">
                    {t('landing-free-study-field-location') || 'Location'}
                  </label>
                  <Input
                    id="free-study-location"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder={
                      t('landing-free-study-field-location-placeholder') || 'e.g. 33.5731, -7.5898'
                    }
                    className="border-2"
                  />
                </div>
                <div className="space-y-1.5">
                  <label
                    htmlFor="free-study-system-type"
                    className="text-xs sm:text-sm font-medium"
                  >
                    {t('landing-free-study-field-system-type') || 'System type'}
                  </label>
                  <Select value={systemType} onValueChange={setSystemType}>
                    <SelectTrigger id="free-study-system-type" className="border-2">
                      <SelectValue
                        placeholder={
                          t('landing-free-study-field-system-type-placeholder') ||
                          'Select a system type'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="total-injection">
                        {t('landing-free-study-system-type-total-injection') || 'Full injection'}
                      </SelectItem>
                      <SelectItem value="surplus-no-storage">
                        {t('landing-free-study-system-type-surplus-no-storage') ||
                          'Surplus injection without storage'}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {!isTotalInjection && (
                  <div className="space-y-1.5">
                    <label
                      htmlFor="free-study-consumption"
                      className="text-xs sm:text-sm font-medium"
                    >
                      {t('landing-free-study-field-consumption') || 'Annual consumption'}
                    </label>
                    <Input
                      id="free-study-consumption"
                      value={consumption}
                      onChange={(e) => setConsumption(e.target.value)}
                      placeholder={
                        t('landing-free-study-field-consumption-placeholder') ||
                        'e.g. 15,000 kWh/year'
                      }
                      className="border-2"
                    />
                  </div>
                )}
                <div className="space-y-1.5">
                  <label htmlFor="free-study-notes" className="text-xs sm:text-sm font-medium">
                    {t('landing-free-study-field-notes') || 'Additional notes'}
                  </label>
                  <Textarea
                    id="free-study-notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder={
                      t('landing-free-study-field-notes-placeholder') ||
                      'Roof type, budget, preferences...'
                    }
                    className="border-2 min-h-[80px] resize-none"
                    rows={3}
                  />
                </div>
                <Button
                  size="xl"
                  onClick={handleSubmit}
                  disabled={!isFormValid}
                  className="w-full touch-manipulation"
                >
                  {t('landing-free-study-cta') || 'Send my project details'}
                  <ArrowRight className="h-5 w-5" />
                </Button>
                <div className="space-y-1 text-center">
                  <p className="text-[10px] sm:text-xs text-muted-foreground">
                    {t('landing-free-study-cta-hint') ||
                      'Opens your email client with pre-filled details'}
                  </p>
                  <p className="flex items-center justify-center gap-1 text-[10px] sm:text-xs text-muted-foreground">
                    <Paperclip className="h-3 w-3 shrink-0" />
                    {t('landing-free-study-attachment-hint') ||
                      'Feel free to attach any relevant files (electricity bills, roof photos, plans…) to this email.'}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
}

function TestimonialsSlider({ t }: { t: TFn }) {
  const { ref, isInView } = useIntersectionObserver();
  const testimonials = [
    {
      quote: t('landing-testimonial-1-quote'),
      name: t('landing-testimonial-1-name'),
    },
    {
      quote: t('landing-testimonial-2-quote'),
      name: t('landing-testimonial-2-name'),
    },
    {
      quote: t('landing-testimonial-3-quote'),
      name: t('landing-testimonial-3-name'),
    },
    {
      quote: t('landing-testimonial-4-quote'),
      name: t('landing-testimonial-4-name'),
    },
    {
      quote: t('landing-testimonial-5-quote'),
      name: t('landing-testimonial-5-name'),
    },
    {
      quote: t('landing-testimonial-6-quote'),
      name: t('landing-testimonial-6-name'),
    },
  ];

  return (
    <section ref={ref} className="landing-section py-16 sm:py-20 md:py-28">
      <div className="mx-auto max-w-6xl px-4">
        <div
          className={cn(
            'mb-8 sm:mb-12 text-center transition-all duration-1000',
            isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          )}
        >
          <Badge
            variant="secondary"
            className="mb-3 sm:mb-4 border-2 border-foreground text-xs sm:text-sm"
          >
            {t('landing-testimonials-eyebrow')}
          </Badge>
          <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl md:text-4xl">
            {t('landing-testimonials-title')}
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-sm sm:text-base text-muted-foreground">
            {t('landing-testimonials-subtitle')}
          </p>
        </div>

        <div
          className={cn(
            'relative transition-all duration-1000 delay-200',
            isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          )}
        >
          <Carousel opts={{ align: 'start', loop: true }} className="w-full">
            <CarouselContent className="py-2">
              {testimonials.map((testimonial) => (
                <CarouselItem key={testimonial.name} className="sm:basis-1/2 lg:basis-1/3">
                  <Card className="h-full border-2 border-border bg-card shadow-sm">
                    <CardContent className="flex h-full flex-col gap-4 p-5 sm:p-6">
                      <div className="text-3xl sm:text-4xl text-primary">"</div>
                      <blockquote className="text-sm sm:text-base leading-relaxed text-muted-foreground">
                        {testimonial.quote}
                      </blockquote>
                      <div className="mt-auto text-sm font-semibold text-foreground">
                        {testimonial.name}
                      </div>
                    </CardContent>
                  </Card>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="hidden sm:flex" />
            <CarouselNext className="hidden sm:flex" />
          </Carousel>
        </div>
      </div>
    </section>
  );
}

function AISection({ onGoToSignup, t }: { onGoToSignup: () => void; t: TFn }) {
  const { ref, isInView } = useIntersectionObserver();

  return (
    <section ref={ref} id="ai" className="landing-section py-16 sm:py-20 md:py-28">
      <div className="mx-auto max-w-6xl px-4">
        <div
          className={cn(
            'mb-8 sm:mb-12 text-center transition-all duration-1000',
            isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          )}
        >
          <Badge
            variant="secondary"
            className="mb-3 sm:mb-4 border-2 border-foreground text-xs sm:text-sm"
          >
            <Sparkles className="mr-1.5 sm:mr-2 h-3 w-3 sm:h-3.5 sm:w-3.5" />
            {t('landing-ai-section-eyebrow') || 'Built-in solar AI'}
          </Badge>
          <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl md:text-4xl lg:text-5xl">
            {t('landing-ai-section-title') || 'Your solar expert, on demand'}
          </h2>
          <p className="mx-auto mt-3 sm:mt-4 max-w-2xl text-base sm:text-lg text-muted-foreground">
            {t('landing-ai-section-subtitle') ||
              'Ask anything. Size instantly. No manual calculations.'}
          </p>
        </div>

        <div className="grid items-start gap-8 lg:grid-cols-2 lg:gap-14">
          {/* Feature cards - show after chat demo on mobile for better visual flow */}
          <div
            className={cn(
              'grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 transition-all duration-700 order-2 lg:order-1',
              isInView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
            )}
          >
            <Card className="border-2 border-border">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="text-base sm:text-lg">
                  {t('landing-ai-feature-1-title') || 'Instant sizing'}
                </CardTitle>
                <CardDescription className="text-sm leading-relaxed">
                  {t('landing-ai-feature-1-desc') ||
                    'Ask “How many panels for 10 kWp?” and get a clear answer in seconds.'}
                </CardDescription>
              </CardHeader>
            </Card>
            <Card className="border-2 border-border">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="text-base sm:text-lg">
                  {t('landing-ai-feature-2-title') || 'ROI analysis'}
                </CardTitle>
                <CardDescription className="text-sm leading-relaxed">
                  {t('landing-ai-feature-2-desc') ||
                    'Instant payback and savings: “What’s the ROI at 1.2 MAD/kWh in Casablanca?”'}
                </CardDescription>
              </CardHeader>
            </Card>
            <Card className="border-2 border-border sm:col-span-2">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="text-base sm:text-lg">
                  {t('landing-ai-feature-3-title') || 'Context-aware'}
                </CardTitle>
                <CardDescription className="text-sm leading-relaxed">
                  {t('landing-ai-feature-3-desc') ||
                    'Your AI understands your project: location, consumption, equipment, and constraints.'}
                </CardDescription>
              </CardHeader>
            </Card>

            <div className="sm:col-span-2 pt-2">
              <Button
                size="xl"
                onClick={onGoToSignup}
                className="w-full sm:w-auto touch-manipulation"
              >
                {t('landing-ai-cta') || 'Try the AI assistant'}
                <ArrowRight className="h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Chat demo - show first on mobile */}
          <div
            className={cn(
              'relative transition-all duration-700 delay-150 order-1 lg:order-2',
              isInView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
            )}
          >
            <div className="absolute -inset-4 rounded-3xl bg-primary/20 blur-2xl" />
            <div className="relative overflow-hidden rounded-xl sm:rounded-2xl border-2 border-border bg-card shadow-xl">
              <div className="border-b border-border bg-muted/40 px-3 sm:px-4 py-2 sm:py-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-muted-foreground">
                    <Sparkles className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary shrink-0" />
                    <span className="truncate">
                      {t('landing-ai-demo-title') || 'SolAI Assistant'}
                    </span>
                  </div>
                  <Badge
                    variant="outline"
                    className="bg-background text-[10px] sm:text-xs shrink-0"
                  >
                    {t('landing-ai-demo-badge') || 'Solar PV expert'}
                  </Badge>
                </div>
              </div>

              <div className="space-y-3 sm:space-y-4 bg-gradient-to-br from-muted/30 to-muted/10 p-4 sm:p-6 md:p-8">
                <div className="flex justify-end">
                  <div className="max-w-[90%] sm:max-w-[85%] rounded-xl sm:rounded-2xl border-2 border-foreground bg-primary px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-primary-foreground shadow-sm">
                    {t('landing-ai-demo-user-message') ||
                      'I need to size a system for 15,000 kWh/year consumption.'}
                  </div>
                </div>
                <div className="flex justify-start">
                  <div className="max-w-[90%] sm:max-w-[85%] rounded-xl sm:rounded-2xl border-2 border-border bg-background px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-foreground shadow-sm">
                    <div className="mb-1.5 sm:mb-2 flex items-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs font-semibold text-muted-foreground">
                      <Sparkles className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-primary" />
                      {t('landing-ai-demo-ai-name') || 'AI Solar Expert'}
                    </div>
                    <div className="whitespace-pre-line leading-relaxed">
                      {t('landing-ai-demo-ai-response') ||
                        'Based on 15,000 kWh/year:\n• Estimated system size: ~10–12 kWp\n• Panels: ~18–22 (550 W)\n• Roof area: ~40–55 m²\nNext: share city + roof tilt/orientation and I’ll estimate production and ROI.'}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs text-muted-foreground">
                  <div className="h-1.5 w-1.5 sm:h-2 sm:w-2 animate-pulse rounded-full bg-primary" />
                  {t('landing-ai-demo-typing') || 'AI is typing…'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PricingCard({
  plan,
  highlight,
  onGoToSignup,
  t,
  isInView,
  index,
}: {
  plan: PlanType;
  highlight?: boolean;
  onGoToSignup: () => void;
  t: TFn;
  isInView: boolean;
  index: number;
}) {
  const config = PLAN_CONFIGS[plan];
  const isEnterprise = isContactUsPlan(plan);
  const isPaidPlan = config.priceMonthly !== null && config.priceMonthly > 0;

  const aiQuotaLabel =
    config.features.maxAiRequestsPerDay === -1
      ? t('landing-ai-quota-unlimited') || 'AI Solar Expert — unlimited consultations'
      : t('landing-ai-quota-per-day', {
          count: config.features.maxAiRequestsPerDay,
        }) || `AI Solar Expert — ${config.features.maxAiRequestsPerDay} consultations/day`;

  const featureLines: { ok: boolean; label: string }[] = [
    {
      ok: true,
      label:
        config.features.maxProjects === -1
          ? t('unlimited-projects') || 'Unlimited projects'
          : `${config.features.maxProjects} ${t('projects-per-month') || 'projects/month'}`,
    },
    {
      ok: true,
      label:
        config.features.maxMembers === -1
          ? t('unlimited-members') || 'Unlimited members'
          : `${config.features.maxMembers} ${t('team-members') || 'team members'}`,
    },
    {
      ok: true,
      label: t('landing-solar-tools') || 'Solar sizing & study tools',
    },
    {
      ok: config.features.aiAssistant,
      label: config.features.aiAssistant
        ? aiQuotaLabel
        : t('landing-ai-not-included') || 'AI Solar Expert — not included',
    },
    {
      ok: config.features.advancedReports,
      label: t('advanced-reports') || 'Advanced Reports',
    },
    { ok: config.features.apiAccess, label: t('api-access') || 'API Access' },
    {
      ok: config.features.prioritySupport,
      label: t('priority-support') || 'Priority Support',
    },
  ];

  if (isEnterprise) {
    featureLines.push(
      { ok: true, label: t('sso') || 'Single Sign-On (SSO)' },
      {
        ok: true,
        label: t('dedicated-account-manager') || 'Dedicated Account Manager',
      },
      { ok: true, label: t('custom-integrations') || 'Custom Integrations' },
      { ok: true, label: t('sla-guarantee') || 'SLA Guarantee' }
    );
  }

  const handleBookDemo = () => {
    window.open(DEMO_CALENDAR_LINK, '_blank');
  };

  return (
    <div
      className={cn(
        'transition-all duration-700',
        isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      )}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <Card
        className={cn(
          'relative h-full overflow-hidden',
          highlight ? 'border-2 border-primary bg-primary/5 shadow-lg' : 'border-2 border-border'
        )}
      >
        {highlight && (
          <div className="absolute -right-14 top-8 rotate-45 bg-foreground px-16 py-1 text-xs font-semibold text-primary">
            {t('landing-popular') || 'POPULAR'}
          </div>
        )}
        <CardHeader className="pb-3 sm:pb-4 p-4 sm:p-6">
          <CardTitle className="text-base sm:text-lg">{config.name}</CardTitle>
          <CardDescription>
            {isEnterprise ? (
              <>
                <span className="text-2xl sm:text-3xl font-bold text-foreground">
                  {t('custom-pricing') || 'Custom'}
                </span>
                <div className="mt-1 text-xs sm:text-sm text-muted-foreground">
                  {t('contact-us-for-pricing') || 'Contact us for pricing'}
                </div>
              </>
            ) : (
              <>
                <span className="text-2xl sm:text-3xl font-bold text-foreground">
                  ${config.priceMonthly}
                </span>
                <span className="ml-1 text-xs sm:text-sm text-muted-foreground">
                  {t('landing-per-month') || '/mo'}
                </span>
                {isPaidPlan && (
                  <span className="ml-1 text-xs sm:text-sm text-muted-foreground">
                    {t('per-user') || 'per user'}
                  </span>
                )}
                <div className="mt-1 text-[10px] sm:text-xs text-muted-foreground">
                  ${config.priceYearly} {t('billed-yearly') || 'billed yearly'}
                </div>
              </>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 sm:space-y-4 p-4 sm:p-6 pt-0">
          <div className="space-y-1.5 sm:space-y-2">
            {featureLines.map((f) => (
              <div key={f.label} className="flex items-start gap-1.5 sm:gap-2 text-xs sm:text-sm">
                <div
                  className={cn(
                    'mt-0.5 inline-flex h-4 w-4 sm:h-5 sm:w-5 shrink-0 items-center justify-center rounded-md border-2',
                    f.ok ? 'border-foreground bg-primary/30' : 'border-border bg-muted'
                  )}
                >
                  <Check
                    className={cn(
                      'h-2.5 w-2.5 sm:h-3.5 sm:w-3.5',
                      f.ok ? 'text-foreground' : 'text-muted-foreground'
                    )}
                  />
                </div>
                <span className={cn(f.ok ? 'text-foreground' : 'text-muted-foreground')}>
                  {f.label}
                </span>
              </div>
            ))}
          </div>

          {isEnterprise ? (
            <Button
              className="w-full touch-manipulation text-xs sm:text-sm h-9 sm:h-10"
              variant="outline"
              onClick={handleBookDemo}
            >
              <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              {t('book-a-demo') || 'Book a demo'}
            </Button>
          ) : (
            <Button
              className="w-full touch-manipulation text-xs sm:text-sm h-9 sm:h-10"
              variant={highlight ? 'default' : 'outline'}
              onClick={onGoToSignup}
            >
              {t('landing-start-trial') || 'Try AI for free'}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SimplifiedPricing({ onGoToSignup, t }: { onGoToSignup: () => void; t: TFn }) {
  const { ref, isInView } = useIntersectionObserver();

  return (
    <section ref={ref} id="pricing" className="landing-section py-16 sm:py-20 md:py-28">
      <div className="mx-auto max-w-6xl px-4">
        <div
          className={cn(
            'mb-8 sm:mb-12 text-center transition-all duration-1000',
            isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          )}
        >
          <Badge
            variant="secondary"
            className="mb-3 sm:mb-4 border-2 border-foreground text-xs sm:text-sm"
          >
            {t('landing-pricing-eyebrow') || 'Transparent pricing'}
          </Badge>
          <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl md:text-4xl">
            {t('landing-pricing-title') || 'Plans for individuals and teams'}
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm sm:text-base text-muted-foreground">
            {t('landing-pricing-subtitle') ||
              'Start with a 30‑day Starter trial, then upgrade as you grow.'}
          </p>
        </div>

        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          <PricingCard
            plan="starter"
            onGoToSignup={onGoToSignup}
            t={t}
            isInView={isInView}
            index={0}
          />
          <PricingCard
            plan="professional"
            highlight
            onGoToSignup={onGoToSignup}
            t={t}
            isInView={isInView}
            index={1}
          />
          <PricingCard
            plan="enterprise"
            onGoToSignup={onGoToSignup}
            t={t}
            isInView={isInView}
            index={2}
          />
        </div>

        <div className="mt-8 flex flex-col items-center justify-center gap-3 text-center">
          <p className="text-sm text-muted-foreground">
            {t('landing-pricing-footnote') ||
              "Need invoicing, procurement, or enterprise onboarding? Contact us — we'll help."}
          </p>
        </div>
      </div>
    </section>
  );
}

function FAQSection({ t }: { t: TFn }) {
  const { ref, isInView } = useIntersectionObserver();

  return (
    <section ref={ref} id="faq" className="landing-section py-16 sm:py-20 md:py-28">
      <div className="mx-auto max-w-6xl px-4">
        <div
          className={cn(
            'mb-8 sm:mb-12 text-center transition-all duration-1000',
            isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          )}
        >
          <Badge
            variant="secondary"
            className="mb-3 sm:mb-4 border-2 border-foreground text-xs sm:text-sm"
          >
            {t('landing-faq-eyebrow') || 'FAQ'}
          </Badge>
          <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl md:text-4xl">
            {t('landing-faq-title') || 'Quick answers'}
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm sm:text-base text-muted-foreground">
            {t('landing-faq-subtitle') || 'A few of the most common questions from new users.'}
          </p>
        </div>

        <div
          className={cn(
            'grid gap-4 lg:grid-cols-2 transition-all duration-700',
            isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          )}
        >
          <Card className="border-2 border-border">
            <CardHeader>
              <CardTitle className="text-lg">
                {t('landing-faq-1-q') || 'What systems are supported today?'}
              </CardTitle>
              <CardDescription className="text-sm leading-relaxed">
                {t('landing-faq-1-a') ||
                  'On-grid PV sizing is available now. Off-grid and solar pumping flows are planned as upcoming modules.'}
              </CardDescription>
            </CardHeader>
          </Card>
          <Card className="border-2 border-border">
            <CardHeader>
              <CardTitle className="text-lg">
                {t('landing-faq-2-q') || 'Can I collaborate with my team?'}
              </CardTitle>
              <CardDescription className="text-sm leading-relaxed">
                {t('landing-faq-2-a') ||
                  'Yes. SolAI supports organizations, member roles, and plan limits for projects and collaborators.'}
              </CardDescription>
            </CardHeader>
          </Card>
          <Card className="border-2 border-border">
            <CardHeader>
              <CardTitle className="text-lg">
                {t('landing-faq-3-q') || 'What can the AI assistant help with?'}
              </CardTitle>
              <CardDescription className="text-sm leading-relaxed">
                {t('landing-faq-3-a') ||
                  'Sizing, production estimates, surface requirements, and economic metrics like payback and ROI — using your project context.'}
              </CardDescription>
            </CardHeader>
          </Card>
          <Card className="border-2 border-border">
            <CardHeader>
              <CardTitle className="text-lg">
                {t('landing-faq-4-q') || 'Is the AI trained for solar PV?'}
              </CardTitle>
              <CardDescription className="text-sm leading-relaxed">
                {t('landing-faq-4-a') ||
                  'It’s optimized for solar PV engineering workflows and speaks your language — technical, structured, and project-aware.'}
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </div>
    </section>
  );
}

function MadeInBadge() {
  return (
    <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
      <span>Made in Morocco & Germany</span>
      {/* Flags will be added here */}
    </div>
  );
}

function FinalCTA({ onGoToSignup, t }: { onGoToSignup: () => void; t: TFn }) {
  const { ref, isInView } = useIntersectionObserver();

  const handleBookDemo = () => {
    window.open(DEMO_CALENDAR_LINK, '_blank');
  };

  return (
    <section ref={ref} className="landing-section py-16 sm:py-20 md:py-28">
      <div className="mx-auto max-w-4xl px-4 text-center">
        <div
          className={cn(
            'transition-all duration-1000',
            isInView ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
          )}
        >
          <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-4xl md:text-5xl lg:text-6xl">
            {t('landing-final-title') || 'Your projects deserve a better workflow.'}
          </h2>
          <p className="mx-auto mt-4 sm:mt-6 max-w-xl text-base sm:text-lg text-muted-foreground">
            {t('landing-final-subtitle') ||
              "Join solar professionals who've upgraded from spreadsheets to a proper engineering workspace — with an AI assistant built in."}
          </p>
          <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <Button
              size="xl"
              onClick={onGoToSignup}
              className="animate-glow w-full sm:w-auto touch-manipulation"
            >
              {t('landing-final-cta') || 'Create your workspace'}
              <ArrowRight className="h-5 w-5" />
            </Button>
            <Button
              size="xl"
              variant="outline"
              onClick={handleBookDemo}
              className="w-full sm:w-auto touch-manipulation"
            >
              <Calendar className="h-5 w-5" />
              {t('book-a-demo') || 'Book a demo'}
            </Button>
          </div>
          <p className="mt-4 text-xs sm:text-sm text-muted-foreground">
            {t('landing-final-note') || 'No credit card required • 30-day trial'}
          </p>
        </div>
      </div>
    </section>
  );
}

export default function LandingPage({ onGoToLogin, onGoToSignup }: LandingPageProps) {
  const { t, i18n } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleBookDemo = () => {
    window.open(DEMO_CALENDAR_LINK, '_blank');
  };

  const blogUrl = `/blog/${i18n.language || 'en'}/`;

  const nav = [
    { href: '#ai', label: t('landing-nav-ai') || 'AI' },
    { href: '#features', label: t('landing-nav-features') || 'Features' },
    { href: '#pricing', label: t('landing-nav-pricing') || 'Pricing' },
    { href: blogUrl, label: t('landing-nav-blog') || 'Blog', external: true },
    { href: '#faq', label: t('landing-nav-faq') || 'FAQ' },
  ];

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SolarGridBackground />
      <SunCursorEffect />

      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-white/10 bg-fastri-dark/95 backdrop-blur-md supports-[backdrop-filter]:bg-fastri-dark/90">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-2 sm:gap-4 px-4 py-2.5 sm:py-3">
          <div className="flex items-center gap-4 sm:gap-6">
            <a href="#" className="flex items-center shrink-0">
              <picture>
                <source
                  type="image/webp"
                  srcSet="/solai-logo-white-100w.webp 1x, /solai-logo-white-200w.webp 2x"
                />
                <img
                  src="/solai-logo-white.png"
                  alt="SolAI - Solar PV Engineering Workspace"
                  className="h-10 sm:h-12 w-auto"
                  width={120}
                  height={61}
                  fetchPriority="high"
                  decoding="async"
                />
              </picture>
            </a>
            <nav className="hidden items-center gap-5 lg:gap-6 md:flex">
              {nav.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  className="text-sm font-medium text-white/70 transition-colors hover:text-white active:text-white"
                  onClick={
                    item.external
                      ? (e) => {
                          e.preventDefault();
                          window.location.href = item.href;
                        }
                      : undefined
                  }
                >
                  {item.label}
                </a>
              ))}
            </nav>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 sm:h-9 sm:w-9 text-white/70 hover:text-white hover:bg-white/10"
                >
                  <Globe2 className="h-4 w-4" />
                  <span className="sr-only">Change language</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => i18n.changeLanguage('en')}
                  className={i18n.language === 'en' ? 'bg-accent' : ''}
                >
                  {t('language-en') || 'English'}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => i18n.changeLanguage('fr')}
                  className={i18n.language === 'fr' ? 'bg-accent' : ''}
                >
                  {t('language-fr') || 'Français'}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => i18n.changeLanguage('de')}
                  className={i18n.language === 'de' ? 'bg-accent' : ''}
                >
                  {t('language-de') || 'Deutsch'}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => i18n.changeLanguage('ar')}
                  className={i18n.language === 'ar' ? 'bg-accent' : ''}
                >
                  {t('language-ar') || 'العربية'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              variant="ghost"
              size="sm"
              onClick={handleBookDemo}
              className="hidden lg:inline-flex text-white/70 hover:text-white hover:bg-white/10"
            >
              <Calendar className="h-4 w-4" />
              {t('book-a-demo') || 'Book a demo'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onGoToLogin}
              className="hidden sm:inline-flex border-white/20 bg-transparent text-white hover:bg-white/10"
            >
              {t('landing-sign-in') || 'Sign in'}
            </Button>
            <Button
              size="sm"
              onClick={onGoToSignup}
              className="hidden xs:inline-flex text-xs sm:text-sm px-2.5 sm:px-3"
            >
              {t('landing-cta') || 'Try the AI free'}
              <ArrowRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            </Button>

            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 sm:h-9 sm:w-9 md:hidden text-white/70 hover:text-white hover:bg-white/10"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile menu panel */}
        <div
          className={cn(
            'md:hidden overflow-hidden transition-all duration-300 ease-in-out border-t border-white/10 bg-fastri-dark',
            mobileMenuOpen ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'
          )}
        >
          <nav className="flex flex-col px-4 py-3 space-y-1">
            {nav.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={(e) => {
                  closeMobileMenu();
                  if (item.external) {
                    e.preventDefault();
                    window.location.href = item.href;
                  }
                }}
                className="py-2.5 px-3 text-base font-medium text-white rounded-lg transition-colors hover:bg-white/10 active:bg-white/5 -mx-1"
              >
                {item.label}
              </a>
            ))}
            <div className="pt-2 border-t border-white/10 mt-2 space-y-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  closeMobileMenu();
                  handleBookDemo();
                }}
                className="w-full justify-center border-white/20 text-white hover:bg-white/10"
              >
                <Calendar className="h-4 w-4" />
                {t('book-a-demo') || 'Book a demo'}
              </Button>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    closeMobileMenu();
                    onGoToLogin();
                  }}
                  className="flex-1 border-white/20 text-white hover:bg-white/10"
                >
                  {t('landing-sign-in') || 'Sign in'}
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    closeMobileMenu();
                    onGoToSignup();
                  }}
                  className="flex-1"
                >
                  {t('landing-cta') || 'Try free'}
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </nav>
        </div>
      </header>

      <main className="relative z-10">
        {/* Hero */}
        <section className="mx-auto max-w-6xl px-4 pb-12 pt-8 sm:pb-16 sm:pt-12 md:pb-20 md:pt-14 lg:pb-24 lg:pt-16">
          <div className="grid items-center gap-8 sm:gap-12 lg:grid-cols-2 lg:gap-16">
            <div className="animate-hero-fade-up">
              <Badge
                variant="secondary"
                className="mb-4 sm:mb-6 border-2 border-foreground px-3 sm:px-4 py-1 sm:py-1.5 text-xs sm:text-sm"
              >
                <Sparkles className="mr-1.5 sm:mr-2 h-3 w-3 sm:h-3.5 sm:w-3.5" />
                {t('landing-eyebrow') || 'AI‑Native Solar Platform'}
              </Badge>

              <h1 className="font-heading text-3xl font-extrabold tracking-tight sm:text-4xl md:text-5xl lg:text-5xl xl:text-6xl">
                {t('landing-hero-title') || 'Design, size, and deliver solar projects faster'}
              </h1>

              <p className="mt-3 sm:mt-5 text-base leading-relaxed text-muted-foreground sm:text-lg md:text-lg lg:text-xl">
                {t('landing-hero-subtitle') ||
                  'Create grid-connected PV studies with reliable meteo inputs, clear economics, and client-ready reports — plus an AI assistant that sizes systems and answers questions instantly.'}
              </p>

              <div className="mt-6 sm:mt-8 flex flex-col gap-3 sm:gap-4 sm:flex-row sm:flex-wrap">
                <Button
                  size="xl"
                  onClick={onGoToSignup}
                  className="animate-pulse-glow w-full sm:w-auto touch-manipulation"
                >
                  {t('landing-cta') || 'Try the AI free'}
                  <ArrowRight className="h-5 w-5" />
                </Button>
                <Button
                  size="xl"
                  variant="outline"
                  onClick={handleBookDemo}
                  className="w-full sm:w-auto touch-manipulation"
                >
                  <Calendar className="h-5 w-5" />
                  {t('book-a-demo') || 'Book a demo'}
                </Button>
              </div>

              <div className="mt-6 sm:mt-8 flex flex-wrap gap-x-4 gap-y-2 sm:gap-6 text-xs sm:text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5 sm:gap-2">
                  <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary shrink-0" />
                  {t('landing-hero-pill-1') || 'Reliable meteo data'}
                </span>
                <span className="flex items-center gap-1.5 sm:gap-2">
                  <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary shrink-0" />
                  {t('landing-hero-pill-2') || 'Multi-language (EN/FR/AR)'}
                </span>
                <span className="flex items-center gap-1.5 sm:gap-2">
                  <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary shrink-0" />
                  {t('landing-hero-pill-3') || 'Built-in AI assistant'}
                </span>
              </div>
            </div>

            <div className="animate-hero-scale-in">
              <HeroVisual t={t} />
            </div>
          </div>
        </section>

        {/* Social Proof */}
        <section className="border-y-2 border-border bg-muted/30 py-6 sm:py-8">
          <div className="mx-auto max-w-6xl px-4">
            <div className="grid grid-cols-2 gap-4 sm:gap-8 text-center sm:grid-cols-4 sm:gap-10">
              <div>
                <div className="text-2xl font-bold sm:text-3xl md:text-4xl">
                  <AnimatedCounter target={450} suffix="+" />
                </div>
                <div className="text-xs sm:text-sm text-muted-foreground">
                  {t('landing-stat-projects') || 'Projects designed'}
                </div>
              </div>
              <div>
                <div className="text-2xl font-bold sm:text-3xl md:text-4xl">
                  <AnimatedCounter target={200} suffix="+" />
                </div>
                <div className="text-xs sm:text-sm text-muted-foreground">
                  {t('landing-stat-users') || 'Solar engineers'}
                </div>
              </div>
              <div>
                <div className="text-2xl font-bold sm:text-3xl md:text-4xl">
                  <AnimatedCounter target={10} suffix="+" />
                </div>
                <div className="text-xs sm:text-sm text-muted-foreground">
                  {t('landing-stat-countries') || 'Countries'}
                </div>
              </div>
              <div>
                <div className="text-2xl font-bold sm:text-3xl md:text-4xl">
                  <AnimatedCounter target={24} suffix="/7" />
                </div>
                <div className="text-xs sm:text-sm text-muted-foreground">
                  {t('landing-stat-support') || 'Support available'}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* AI */}
        <AISection onGoToSignup={onGoToSignup} t={t} />

        {/* Before/After */}
        <BeforeAfterSection t={t} />

        {/* Features */}
        <section id="features" className="landing-section py-20 sm:py-28">
          <div className="mx-auto max-w-6xl px-4">
            <FeatureSection
              icon={Sun}
              title={t('landing-feature-1-title') || 'PV system sizing'}
              description={
                t('landing-feature-1-desc') ||
                'Configure panels, inverters, and losses for full control — or let the AI size from consumption data. Backed by reliable meteo inputs.'
              }
              visual={
                <div className="overflow-hidden rounded-2xl border-2 border-border bg-card shadow-lg">
                  <picture>
                    <source
                      type="image/webp"
                      srcSet="/images/feature-sizing-400w.webp 400w, /images/feature-sizing-524w.webp 524w, /images/feature-sizing-600w.webp 600w"
                      sizes="(max-width: 640px) 400px, 524px"
                    />
                    <img
                      src="/images/feature-sizing.jpg"
                      alt="Solar panel system sizing interface"
                      className="h-auto w-full object-cover"
                      loading="lazy"
                      width={600}
                      height={400}
                      decoding="async"
                    />
                  </picture>
                </div>
              }
              index={0}
            />

            <FeatureSection
              icon={BarChart3}
              title={t('landing-feature-2-title') || 'Economic & environmental studies'}
              description={
                t('landing-feature-2-desc') ||
                'Model project economics with ROI, payback, and savings. The AI helps answer questions and compute scenarios.'
              }
              visual={
                <div className="overflow-hidden rounded-2xl border-2 border-border bg-card shadow-lg">
                  <picture>
                    <source
                      type="image/webp"
                      srcSet="/images/feature-economic-400w.webp 400w, /images/feature-economic-524w.webp 524w, /images/feature-economic-600w.webp 600w"
                      sizes="(max-width: 640px) 400px, 524px"
                    />
                    <img
                      src="/images/feature-economic.jpg"
                      alt="Economic study dashboard with ROI analysis"
                      className="h-auto w-full object-cover"
                      loading="lazy"
                      width={600}
                      height={400}
                      decoding="async"
                    />
                  </picture>
                </div>
              }
              reverse
              index={1}
            />

            <FeatureSection
              icon={Globe2}
              title={t('landing-feature-3-title') || 'Reliable climate data'}
              description={
                t('landing-feature-3-desc') ||
                'Powered by trusted irradiation and weather databases. Your sizing and production estimates are defensible and accurate.'
              }
              visual={
                <div className="overflow-hidden rounded-2xl border-2 border-border bg-card shadow-lg">
                  <picture>
                    <source
                      type="image/webp"
                      srcSet="/images/feature-reports-400w.webp 400w, /images/feature-reports-524w.webp 524w, /images/feature-reports-600w.webp 600w"
                      sizes="(max-width: 640px) 400px, 524px"
                    />
                    <img
                      src="/images/feature-reports.jpg"
                      alt="Professional PDF report generation"
                      className="h-auto w-full object-cover"
                      loading="lazy"
                      width={600}
                      height={400}
                      decoding="async"
                    />
                  </picture>
                </div>
              }
              index={2}
            />
          </div>
        </section>

        {/* Free Study */}
        <FreeStudySection t={t} />

        {/* Testimonials */}
        <TestimonialsSlider t={t} />

        {/* Pricing */}
        <SimplifiedPricing onGoToSignup={onGoToSignup} t={t} />

        {/* FAQ */}
        <FAQSection t={t} />

        {/* Final CTA */}
        <FinalCTA onGoToSignup={onGoToSignup} t={t} />

        {/* Blog Preview */}
        <BlogPreviewSection />

        {/* Footer */}
        <footer className="border-t-2 border-border bg-muted/20">
          <div className="mx-auto flex max-w-6xl flex-col gap-6 px-4 py-8 sm:py-12">
            {/* Logo and tagline */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                <picture>
                  <source
                    type="image/webp"
                    srcSet="/solai-logo-100w.webp 1x, /solai-logo-200w.webp 2x"
                  />
                  <img
                    src="/solai-logo.png"
                    alt="SolAI"
                    className="h-11 sm:h-12 w-auto"
                    width={120}
                    height={61}
                    loading="lazy"
                    decoding="async"
                  />
                </picture>
                <div className="text-xs sm:text-sm text-muted-foreground">
                  {t('landing-footer', 'Solar PV engineering workspace')}
                </div>
              </div>
              {/* Made in Morocco & Germany badge */}
              <MadeInBadge />
            </div>

            {/* Navigation links */}
            <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-4 sm:gap-6">
              {/* Main nav links */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 sm:gap-6">
                <a
                  className="text-xs sm:text-sm text-muted-foreground transition-colors hover:text-foreground active:text-foreground py-1"
                  href="#features"
                >
                  {t('landing-nav-features') || 'Features'}
                </a>
                <a
                  className="text-xs sm:text-sm text-muted-foreground transition-colors hover:text-foreground active:text-foreground py-1"
                  href="#pricing"
                >
                  {t('landing-nav-pricing') || 'Pricing'}
                </a>
                <a
                  className="text-xs sm:text-sm text-muted-foreground transition-colors hover:text-foreground active:text-foreground py-1"
                  href={blogUrl}
                >
                  {t('landing-nav-blog') || 'Blog'}
                </a>
                <a
                  className="text-xs sm:text-sm text-muted-foreground transition-colors hover:text-foreground active:text-foreground py-1"
                  href={LINKEDIN_COMPANY_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('landing-nav-linkedin') || 'LinkedIn'}
                </a>
                <a
                  className="text-xs sm:text-sm text-muted-foreground transition-colors hover:text-foreground active:text-foreground py-1"
                  href="#faq"
                >
                  {t('landing-nav-faq') || 'FAQ'}
                </a>
                <button
                  className="text-xs sm:text-sm font-semibold text-muted-foreground hover:text-foreground active:text-foreground cursor-pointer py-1 touch-manipulation"
                  onClick={handleBookDemo}
                >
                  {t('book-a-demo') || 'Book a demo'}
                </button>
                <a
                  className="text-xs sm:text-sm font-semibold text-muted-foreground hover:text-foreground active:text-foreground py-1"
                  href="mailto:contact@solaipv.com"
                >
                  {t('contact-support') || 'Contact'}
                </a>
                <a
                  className="text-xs sm:text-sm font-semibold text-muted-foreground hover:text-foreground active:text-foreground py-1"
                  href="/free-study"
                >
                  {t('landing-nav-free-study') || 'Free study'}
                </a>
              </div>

              {/* Legal links */}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 sm:gap-x-6 pt-2 sm:pt-0 border-t sm:border-t-0 border-border/50 sm:border-l sm:pl-6">
                <a
                  className="text-xs sm:text-sm text-muted-foreground hover:text-foreground active:text-foreground py-1"
                  href="/legal/terms"
                >
                  {t('legal-terms-of-use') || 'Terms'}
                </a>
                <a
                  className="text-xs sm:text-sm text-muted-foreground hover:text-foreground active:text-foreground py-1"
                  href="/legal/legal-notice"
                >
                  {t('legal-legal-notice') || 'Legal notice'}
                </a>
                <a
                  className="text-xs sm:text-sm text-muted-foreground hover:text-foreground active:text-foreground py-1"
                  href="/legal/privacy"
                >
                  {t('legal-privacy-policy') || 'Privacy'}
                </a>
                <a
                  className="text-xs sm:text-sm text-muted-foreground hover:text-foreground active:text-foreground py-1"
                  href="/legal/cookies"
                >
                  {t('legal-cookie-policy') || 'Cookies'}
                </a>
              </div>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
}
